/**
 * 是否是base64
 */
const isBase64 = (src: string) => {
    return /^data:/.test(src);
};

/**
 * 浏览器是否支持 webp
 */
const checkIsSupportWebp = () => {
    try {
        return (
            document
                .createElement('canvas')
                .toDataURL('image/webp', 0.5)
                .indexOf('data:image/webp') === 0
        );
    } catch (err) {
        return false;
    }
};

const isSupportWebp = checkIsSupportWebp();

const addQuery = (url: string, query: string) => {
    let prefix = '';

    if (url.indexOf('?') !== -1) {
        prefix = '&';
    } else {
        prefix = '?';
    }

    return url + prefix + query;
};

/**
 * 匹配 background url
 */
const reg = /url\([^]+\)/g;
/**
 * 缩放的webp图片query
 */
const miniWebpQuery = 'x-s3-process=image/format,webp';
/**
 * 高清的webp图片query
 */
const webpQuery = 'x-s3-process=image/format,webp';
/**
 * 缩放的原图格式图片query
 */
const miniQuery = 'x-s3-process=image/resize,p_10';

/**
 * 缩略图地址
 */
const miniSrc = (originSrc: string) => {
    if (isSupportWebp) {
        return addQuery(originSrc, miniWebpQuery);
    }

    return addQuery(originSrc, miniQuery);
};

/**
 * 高清图地址
 */
const hdSrc = (originSrc: string) => {
    if (isSupportWebp) {
        return addQuery(originSrc, webpQuery);
    }

    return originSrc;
};

/**
 *
 * @param originString 图片地址
 * @param effect 渐进的操作dom, 使图片平滑加载
 * @param useWebp 是否使用webp
 * @param opacity 初始的透明度
 */
const preLoadImgFactory = function (
    originSrc: string,
    effect: Function,
    useWebp: boolean,
    opacity: string
) {
    return function (el: HTMLElement) {
        // 如果是base64
        if (isBase64(originSrc)) {
            return effect(originSrc);
        }

        // 处理后的图片
        const effectedSrc = useWebp ? hdSrc(originSrc) : originSrc;

        // 加载小图
        effect(miniSrc(originSrc));
        el.style.opacity = opacity;
        const img = new Image();

        img.src = effectedSrc;
        // 图片加载完成
        img.onload = () => {
            /**
             * 加载大图
             * 这里做了部分兼容, 如果css里有写background-image的话, 这里需要把元素里的background-image清空, 避免在禁用cache的时候, 图片抖动
             */
            el.tagName.toLocaleLowerCase() !== 'img' && !useWebp
                ? (el.style.backgroundImage = '')
                : effect(effectedSrc);
            el.style.opacity = '1';
            el.style.transition = 'opacity 0.7s';
        };
    };
};

/**
 * 处理<img c-webp src="xx">的情况
 */
const processImgTag = (el: HTMLElement) => {
    const originSrc = el.getAttribute('src') || '';

    if (originSrc) {
        el.dataset.webpCache = originSrc;
        const effect = function (src: string) {
            el.setAttribute('src', src);
        };

        const preload = preLoadImgFactory(originSrc, effect, true, '.5');

        preload(el);
    }
};

/**
 * 处理css里的图片
 * @param srcString: 表示v-webp里的表达式
 */
const processCssBackground = function (el: HTMLElement, srcString: string) {
    const cssProps = window.getComputedStyle(el, null);
    const hasBgImg = cssProps.getPropertyValue('background').includes('url(');
    let originSrc = '';
    let useWebp = false;

    // 如果直接指定background-image了的话, 浏览器会直接加载原图, 没法阻止, 这个时候就不能用webp了, 避免浪费流量
    if (hasBgImg) {
        const url = cssProps.getPropertyValue('background').match(reg);

        if (url) {
            originSrc = url[0].split('"')[1];
        }
    } else if (srcString) {
        originSrc = srcString;
        // 在v-imgPro="xxx" 里的图片才能享受 webp压缩
        useWebp = true;
    }

    if (!originSrc) return;
    el.dataset.webpCache = originSrc;
    const effect = function (src: string) {
        el.style.backgroundImage = 'url("' + src + '")';
    };

    const preload = preLoadImgFactory(originSrc, effect, useWebp, '.81');

    preload(el);
};

const processImg = (el: HTMLElement, imgSrc?: string) => {
    // 避免子组件更新, 造成重复的图片处理
    const webpCache = el.dataset.webpCache;

    if (webpCache) {
        return;
    }

    /**
     * 判断 dom 节点类型
     */
    if (el.tagName.toLocaleLowerCase() === 'img') {
        /**
         * 处理img标签
         */
        processImgTag(el);
    } else if (imgSrc) {
        /**
         * 处理css里的图片
         */
        processCssBackground(el, imgSrc);
    }
};

const webp = (el: HTMLElement, imgSrc?: string) => {
    // install
    processImg(el, imgSrc);

    return {
        update(newImgSrc: string) {
            // update
            processImg(el, newImgSrc);
        }
    };
};

export default webp;
